/* Copyright (c) 2017 Backpack Health LLC */
/* global window */

import React from 'react';

import ShareCardService from '../../../../apiServices/ShareCardService';
import { publicPasscode, publicHash } from '../../../../utilities/LocalStorage';
import ManageSession from '../../../../utilities/ManageSession';
import AppUtils from '../../../../utilities/AppUtils';
import { getLocalText } from '../../../../utilities/localizedUtil';

import PasscodeEntry from '../PasscodeEntry/PasscodeEntry';
import Inactive from '../PasscodeEntry/Inactive';
import PublicShareCard from './PublicShareCard';
import Invalid from '../PasscodeEntry/Invalid';
import ProfileModel from '../../../../models/ProfileModel';
import { useActiveUser } from '../../../hooks/useUser';
import { useLists } from '../../../hooks/useLists';
import { useUIState } from '../../../hooks/useUIState';
import ContentLoader from '../../../components/ContentLoader';

interface Props {
  hash: string;
  accessCode?: string;
  requiresPasscode: boolean;
  vendorName?: string;
}


function getPageTitlePhrase(shareCard, languageCode) {
  const profile = new ProfileModel(shareCard.profile);
  const shareCardName = getLocalText(shareCard.metadata.title, languageCode);

  return profile.fullName() ? {
    id: 'shareCardURL.pageTitleWithName.text',
    values: {
      string1: profile.fullName(),
      string2: shareCardName,
    },
  } : {
    id: 'shareCardURL.pageTitleWithoutName.text',
    values: {
      string1: shareCardName,
    },
  };
}
const PublicShareCardController: React.FC<Props> = (props) => {
  const { hash, requiresPasscode, vendorName } = props;

  const [, uiActions] = useUIState();
  const [{ user }] = useActiveUser();
  const [listState] = useLists();
  const localeId = user.locale_id;
  const locale = listState.localeLookup[localeId];
  const languageCode = locale.language_code;
  const cancelIdleTimerRef = React.useRef(null);

  const [state, setState] = React.useState({
    isLoading: true,
    isNotFound: false,
    isInactive: false,
    isUnauthorized: false,
    metadata: {},
    accessCode: '',
  });

  const receiveShareCardMetadata = (metadata) => {
    uiActions.setWindowTitlePhrase(getPageTitlePhrase(metadata, languageCode));

    const startIdleTimer = () => {
      if (!requiresPasscode) { return; }

      cancelIdleTimerRef.current = ManageSession.startPublicIdleTimer(() => {
        AppUtils.clearSavedData();

        setState({
          ...state,
          isInactive: true,
          isUnauthorized: true,
          isNotFound: false,
        });
      });
    };

    startIdleTimer();

    setState({
      ...state,
      isNotFound: false,
      isUnauthorized: false,
      isInactive: false,
      metadata: {
        ...metadata,
      },
      isLoading: false,
    });
  };

  const requestShareCardMetadata = (encodedAccessCode: string, successCallback) => {
    setState({
      ...state,
      isLoading: true,
    });
    ShareCardService.getPublicMetadata(hash, encodedAccessCode, languageCode, (apiError, res) => {
      if (apiError) {
        const isNotFound = apiError.isNotFound();
        setState({
          ...state,
          isNotFound,
          isUnauthorized: apiError.isUnauthorized(),
          isLoading: false,
        });

        if (isNotFound) {
          return;
        }
      } else {
        const accessCode = window.atob(encodedAccessCode).split('').filter(a => a !== ':').join('');
        setState({
          ...state,
          accessCode,
        });
      }
      successCallback(apiError, res);
    });
  };

  React.useEffect(() => {
    const savedPasscode = publicPasscode.get() || '';
    if (requiresPasscode && props.accessCode) {
      const encodedPassCode = window.btoa(`:${props.accessCode}`);
      requestShareCardMetadata(encodedPassCode, (apiError, res) => {
        if (apiError) {
          setState({
            ...state,
            isLoading: false,
            isNotFound: true,
          });
          return;
        }
        receiveShareCardMetadata(res);
      });
    } else if (!requiresPasscode || savedPasscode) {
      requestShareCardMetadata(savedPasscode, (apiError, res) => {
        if (apiError) { return; }
        receiveShareCardMetadata(res);
      });
    } else {
      setState({
        ...state,
        isLoading: false,
        isUnauthorized: true,
      });
    }
  }, [languageCode]);

  React.useEffect(() => {
    const existingHash = publicHash.get();
    if (existingHash !== hash) {
      publicHash.set(hash);
      publicPasscode.remove();
    }
  }, []);

  if (state.isInactive) { return <Inactive onRetry={() => { setState({ ...state, isNotFound: false, isUnauthorized: true, isInactive: false }); }} />; }

  if (state.isNotFound) {
    return requiresPasscode ? (
      <Invalid />
      ) : (
      <Invalid
        pageTitlePhraseId="shareCardURLInvalid.stickyj.page.title"
        titlePhraseId="shareCardURLInvalid.stickyj.title"
        messagePhraseId="shareCardURLInvalid.shareCard.stickyj.message.md"
        contactVendor={vendorName}
        isMarkdown={true}
      />
      );
  }

  return (
    state.isUnauthorized ? (
      <PasscodeEntry
        hash={hash}
        onSubmit={requestShareCardMetadata}
        onSuccess={receiveShareCardMetadata}
        onNotFound={() => { setState({ ...state, isNotFound: true }); }}
      />
    ) : (
      <ContentLoader isContentLoaded={!state.isLoading} isFixed={true} coverWholeScreen={true}>
        <PublicShareCard
          hash={hash}
          requiresPasscode={requiresPasscode}
          languageCode={languageCode}
          metadata={state.metadata}
          accessCode={state.accessCode}
          isAccessCodeInUrl={!!props.accessCode}
        />
      </ContentLoader>
    )
  );
};

export const PasscodeAccessiblePublicShareCard = (props) => {
  const { hash } = props.match.params;
  const searchParams = AppUtils.getQueryParams(props.location.search);
  return (
    <PublicShareCardController
      hash={hash}
      vendorName={null}
      requiresPasscode={true}
      accessCode={searchParams.access_code}
    />
  );
};

export const JewelryAccessiblePublicShareCard = (props) => {
  const { vendorName, hash } = props.match.params;
  const searchParams = AppUtils.getQueryParams(props.location.search);

  return (
    <PublicShareCardController
      hash={`${vendorName}/${hash}`}
      vendorName={vendorName}
      requiresPasscode={false}
      accessCode={searchParams.access_code}
    />
  );
};

export default PublicShareCardController;
